/**
 * The assistant (UC-A1), built to `design/new design /legacy/bank_ai_assistant_chat`.
 *
 * ## Three columns, and what happens to them on a phone
 *
 * Conversation rail — transcript — sources. Every breakpoint below is transcribed from the design
 * rather than chosen here:
 *
 * * the rail is `hidden lg:flex`, and below `lg` it is reached from the mobile header;
 * * the sources panel is `fixed … translate-x-full` with a scrim, and `lg:relative` — a drawer on
 *   a phone and the third column on a desktop, from one element;
 * * the header is two elements, `hidden lg:flex` and `flex lg:hidden`, because the desktop one
 *   carries the conversation title and a Sources toggle while the mobile one carries the menu and
 *   a badge count;
 * * the transcript is `bg-[#f8fafc] lg:bg-white`, which is the design's own choice and reads as a
 *   sheet of paper on a large screen and a list of cards on a small one.
 *
 * ## Why this page owns its layout instead of sitting inside `AppShell`
 *
 * The shell's rail is *navigation*. The design's rail is *conversation history*, which is a
 * different thing serving a different need — an agent works inside one conversation for a whole
 * call and wants the last thing they asked at arm's length, not a link to the screen they are on.
 * Putting history into a nav component would have made both worse.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ApiError,
  MenuIcon,
  SourcesIcon,
  cx,
  fetchSessionMessages,
  fetchSessions,
  useLanguage,
  type SessionMessage,
  type SessionSummary,
} from "@nab/shared";

import { Composer } from "../components/Composer";
import { ConversationRail } from "../components/ConversationRail";
import { Message } from "../components/Message";
import { SourcePanel } from "../components/SourcePanel";
import { useChat, type ChatMessage } from "../hooks/useChat";

/** A stored message, as the transcript renders it. See `openSession` for the defaults. */
function asChatMessage(stored: SessionMessage): ChatMessage {
  return {
    id: stored.message_id,
    role: stored.role,
    content: stored.content,
    language: stored.language,
    outcome: stored.outcome,
    citations: stored.citations,
    // Not stored with the message, and not reconstructable from it. Both are properties of the
    // turn as it happened rather than of the text, and inventing either on a reread would put
    // words in the bank's mouth.
    regulatoryDisclaimer: null,
    suggestedDepartment: null,
    streaming: false,
    auditFailure: false,
    featureOffer: null,
  };
}

export interface ChatPageProps {
  employeeId?: string;
}

export function ChatPage({ employeeId }: ChatPageProps): JSX.Element {
  const { t } = useTranslation("app");
  const { t: common } = useTranslation();
  const { direction } = useLanguage();
  const { messages, sessionId, sending, send, stop, reset, load } = useChat(
    employeeId ? { employeeId } : {},
  );

  const [selectedCitation, setSelectedCitation] = useState<number | null>(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(false);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const transcriptRef = useRef<HTMLDivElement>(null);

  /**
   * Open a stored conversation in the transcript.
   *
   * The rail used to take the id and call `reset()` with it — so clicking yesterday's call
   * started a new empty conversation instead of opening it. The endpoint, the client function and
   * the hook's `load` all existed; nothing joined them.
   *
   * A stored turn is not a live one, so the fields that only a stream produces are filled with
   * what they mean after the fact: nothing is arriving (`streaming: false`), the turn was recorded
   * or it would not be here (`auditFailure: false`), and a feature offer is a live suggestion that
   * expires with its turn rather than something to re-offer on a reread.
   */
  const openSession = useCallback(
    async (id: string) => {
      if (id === sessionId) return;
      try {
        const stored = await fetchSessionMessages(id, employeeId ? { employeeId } : {});
        load(id, stored.map(asChatMessage));
      } catch (caught) {
        // Leave the employee where they are. Replacing a readable transcript with an error
        // because the *next* one would not load is the worse of the two outcomes.
        if (!(caught instanceof ApiError)) throw caught;
      }
    },
    [employeeId, load, sessionId],
  );

  // The employee's own conversations, for the rail. Refreshed when a turn completes so a new
  // conversation appears in the list without a reload.
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const found = await fetchSessions(employeeId ? { employeeId } : {});
        if (live) setSessions(found);
      } catch (caught) {
        // A rail that cannot load its history is a rail with no history in it, not a broken page.
        if (!(caught instanceof ApiError)) return;
      }
    })();
    return () => {
      live = false;
    };
  }, [employeeId, sending]);

  // Sources come from the most recent assistant message that has any: the panel reflects the
  // answer being read, and carrying stale sources forward would attach the wrong documents to it.
  const activeCitations = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message && message.role === "assistant" && message.citations.length > 0) {
        return message.citations;
      }
    }
    return [];
  }, [messages]);

  // Follow the stream, but only when the employee is already at the bottom: yanking the view down
  // while they are reading an earlier answer is worse than not following.
  useEffect(() => {
    const node = transcriptRef.current;
    if (!node) return;
    const nearBottom = node.scrollHeight - node.scrollTop - node.clientHeight < 120;
    if (nearBottom) node.scrollTop = node.scrollHeight;
  }, [messages]);

  const onCitationClick = useCallback((index: number) => {
    setSelectedCitation((current) => (current === index ? null : index));
    // On a phone the panel is a drawer, so clicking a marker has to open it — otherwise the
    // marker appears to do nothing, which is the failure that makes citations look decorative.
    setSourcesOpen(true);
  }, []);

  // The conversation's own name, or the first thing asked in it. The design puts this in the
  // header of both breakpoints, and an untitled conversation there would waste the one line an
  // agent uses to tell two calls apart.
  const title =
    sessions.find((session) => session.session_id === sessionId)?.title?.trim() ||
    messages.find((message) => message.role === "user")?.content ||
    t("chat.heading");

  return (
    <div className="flex h-screen min-h-0 w-full overflow-hidden bg-surfaceRaised" dir={direction}>
      <ConversationRail
        sessions={sessions}
        activeSessionId={sessionId}
        onSelect={(id) => {
          setRailOpen(false);
          void openSession(id);
        }}
        onNew={() => {
          setRailOpen(false);
          reset();
        }}
        className="hidden lg:flex"
      />

      {/* The rail as a drawer, below `lg`. Same component, so the two never drift apart. */}
      <div
        aria-hidden="true"
        onClick={() => setRailOpen(false)}
        className={cx(
          "fixed inset-0 z-[90] bg-surfaceInverse/50 backdrop-blur-sm transition-opacity lg:hidden",
          railOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      <div
        className={cx(
          "fixed inset-y-0 start-0 z-[91] transition-transform duration-300 lg:hidden",
          railOpen ? "translate-x-0" : "-translate-x-full rtl:translate-x-full",
        )}
      >
        <ConversationRail
          sessions={sessions}
          activeSessionId={sessionId}
          onSelect={(id) => {
            setRailOpen(false);
            void openSession(id);
          }}
          onNew={() => {
            setRailOpen(false);
            reset();
          }}
          className="shadow-2xl"
        />
      </div>

      <main className="relative flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-surfaceRaised">
        {/* Desktop header: the conversation's name, and the Sources toggle. */}
        <header className="z-10 hidden h-[72px] shrink-0 items-center justify-between border-b border-borderSubtle bg-surfaceRaised/80 px-6 backdrop-blur-xl lg:flex">
          <h1 className="truncate text-body-md font-bold tracking-tight text-primary">
            <bdi>{title}</bdi>
          </h1>
          {activeCitations.length > 0 ? (
            <button
              type="button"
              onClick={() => setSourcesOpen((open) => !open)}
              title={common("citations.heading")}
              className="flex h-9 items-center gap-2 rounded-xl border border-borderStrong bg-surfaceRaised px-3 text-body-sm font-semibold text-primary transition-all hover:bg-primarySubtle"
            >
              <SourcesIcon />
              <span>{common("citations.heading")}</span>
              <span className="rounded-full border border-primaryBorder bg-primarySubtle px-2 py-1 text-label-sm font-bold text-primary">
                {activeCitations.length}
              </span>
            </button>
          ) : null}
        </header>

        {/* Mobile header: the menu, the name, and the Sources count as a badge. */}
        <div className="z-10 flex w-full shrink-0 items-center justify-between gap-2 border-b border-borderStrong bg-surfaceRaised p-3 shadow-sm md:p-4 lg:hidden">
          <button
            type="button"
            onClick={() => setRailOpen(true)}
            title={t("chat.conversations")}
            aria-label={t("chat.conversations")}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-borderStrong bg-surfaceRaised text-primary shadow-sm"
          >
            <MenuIcon />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-body-md font-bold tracking-tight text-primary">
            <bdi>{title}</bdi>
          </h1>
          {activeCitations.length > 0 ? (
            <button
              type="button"
              onClick={() => setSourcesOpen(true)}
              title={common("citations.heading")}
              aria-label={common("citations.heading")}
              className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-borderStrong bg-surfaceRaised text-primary shadow-sm"
            >
              <SourcesIcon />
              <span className="absolute -end-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[9px] font-bold text-onPrimary">
                {activeCitations.length}
              </span>
            </button>
          ) : null}
        </div>

        <div
          className="min-h-0 w-full flex-1 overflow-y-auto bg-surface lg:bg-surfaceRaised"
          ref={transcriptRef}
          role="log"
          aria-label={t("chat.transcript")}
        >
          <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4 md:p-6 lg:p-8">
            {messages.length === 0 ? (
              <EmptyConversation />
            ) : (
              messages.map((message) => (
                <Message
                  key={message.id}
                  message={message}
                  onCitationClick={onCitationClick}
                  sessionId={sessionId}
                  {...(employeeId ? { employeeId } : {})}
                />
              ))
            )}
          </div>
        </div>

        <div className="z-10 w-full shrink-0 border-t border-borderStrong bg-surfaceRaised px-4 py-3 shadow-[0_-4px_10px_rgba(0,0,0,0.02)] md:px-8">
          <div className="mx-auto w-full max-w-5xl">
            <Composer
              onSend={send}
              onStop={stop}
              sending={sending}
              sessionId={sessionId}
            />
            <p className="mt-2 text-center text-label-sm text-textMuted">
              {t("chat.languageHint")}
            </p>
          </div>
        </div>
      </main>

      <SourcePanel
        citations={activeCitations}
        selectedIndex={selectedCitation}
        onSelect={setSelectedCitation}
        open={sourcesOpen}
        onClose={() => setSourcesOpen(false)}
      />
    </div>
  );
}

/** The empty state. Says what the assistant is for, once, to someone who has not used it. */
function EmptyConversation(): JSX.Element {
  const { t } = useTranslation("app");

  return (
    <div className="mx-auto flex max-w-xl flex-col items-center gap-3 py-16 text-center">
      <h2 className="text-heading-md text-textPrimary">{t("chat.emptyState")}</h2>
      <p className="text-body-md text-textMuted">{t("chat.intro")}</p>
      <ul className="mt-2 flex flex-col gap-1 text-body-sm text-textMuted">
        <li>{t("chat.exampleFee")}</li>
        <li>{t("chat.exampleProcedure")}</li>
        <li>{t("chat.exampleCircular")}</li>
      </ul>
    </div>
  );
}
